import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';
import { useAuthStore } from '../../store/useAuthStore';
import type { Tables } from '../../types/database.types';


// ============================================================
// Types
// ============================================================

export interface Channel {
  id: string;
  name: string | null;
  kind: 'global' | 'dm' | 'dossier';
  isPrivate: boolean;
  createdBy: string;
  createdAt: string;
  description: string | null;
  emoji: string;
  archivedAt: string | null;
  dossierId: string | null;
  // Computed
  unreadCount?: number;
  lastMessage?: Pick<Message, 'body' | 'createdAt' | 'author'>;
  dmOtherCollaborator?: Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'>;
  /** last_read_at de l'utilisateur courant pour ce canal. Sert au scroll initial. */
  lastReadAt?: string | null;
}

export interface Message {
  id: string;
  channelId: string;
  authorId: string;
  body: string;
  parentId: string | null;
  isEdited: boolean;
  editedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  /** Date d'épinglage dans le salon, null si le message n'est pas épinglé. */
  pinnedAt: string | null;
  author?: Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'>;
}

export interface CollaboratorSummary {
  id: string;
  full_name: string;
  avatar_url: string | null;
  role: string;
}

// ============================================================
// Internal helpers
// ============================================================

function getCurrentCollaboratorId(): string | null {
  return useAuthStore.getState().collaborator?.id ?? null;
}

function mapMessage(row: Record<string, unknown>): Message {
  const authorRow = row.author as Record<string, unknown> | null;
  return {
    id: row.id as string,
    channelId: row.channel_id as string,
    authorId: row.author_id as string,
    body: row.body as string,
    parentId: (row.parent_id as string | null) ?? null,
    isEdited: (row.is_edited as boolean) ?? false,
    editedAt: (row.edited_at as string | null) ?? null,
    deletedAt: (row.deleted_at as string | null) ?? null,
    createdAt: row.created_at as string,
    pinnedAt: (row.pinned_at as string | null) ?? null,
    author: authorRow
      ? {
          id: authorRow.id as string,
          full_name: authorRow.full_name as string,
          avatar_url: (authorRow.avatar_url as string | null) ?? null,
        }
      : undefined,
  };
}

function mapChannel(row: Record<string, unknown>): Channel {
  return {
    id: row.id as string,
    name: (row.name as string | null) ?? null,
    kind: row.kind as 'global' | 'dm' | 'dossier',
    isPrivate: (row.is_private as boolean) ?? false,
    createdBy: row.created_by as string,
    createdAt: row.created_at as string,
    description: (row.description as string | null) ?? null,
    emoji: (row.emoji as string) ?? 'hashtag',
    archivedAt: (row.archived_at as string | null) ?? null,
    dossierId: (row.dossier_id as string | null) ?? null,
  };
}

// ============================================================
// Channels
// ============================================================

export async function getChannels(): Promise<Channel[]> {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) throw new Error('Not authenticated');

  // Get channels where user is a member
  const { data: memberships, error: mErr } = await supabase
    .from('channel_members')
    .select('channel_id, last_read_at')
    .eq('collaborator_id', currentId);

  if (mErr) throw mErr;
  if (!memberships?.length) return [];

  const channelIds = memberships.map((m) => m.channel_id);
  const lastReadMap = new Map<string, string>(
    memberships.map((m) => [m.channel_id, m.last_read_at]),
  );

  // Les canaux 'dossier' sont exclus de la messagerie : ils vivent dans l'onglet
  // Discussions du dossier (DossierDiscussionTab) pour éviter la pollution et
  // garder la messagerie performante quand le cabinet grandit.
  const { data: channels, error: chErr } = await supabase
    .from('discussion_channels')
    .select('*')
    .in('id', channelIds)
    .is('archived_at', null)
    .neq('kind', 'dossier')
    .order('created_at', { ascending: true });

  if (chErr) throw chErr;

  const mapped = (channels ?? []).map((c) => mapChannel(c as unknown as Record<string, unknown>));

  // Fetch unread counts in parallel
  const unreadResults = await Promise.all(
    mapped.map(async (ch) => {
      const lastRead = lastReadMap.get(ch.id) ?? new Date(0).toISOString();
      const { count } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .eq('channel_id', ch.id)
        .is('deleted_at', null)
        .gt('created_at', lastRead)
        .neq('author_id', currentId);
      return { id: ch.id, count: count ?? 0 };
    }),
  );

  const unreadMap = new Map(unreadResults.map((r) => [r.id, r.count]));

  // For DM channels, fetch the other collaborator's info
  const dmChannels = mapped.filter((c) => c.kind === 'dm');
  const dmOtherMap = new Map<string, Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'>>();

  if (dmChannels.length > 0) {
    const { data: dmMembers } = await supabase
      .from('channel_members')
      .select('channel_id, collaborator_id, collaborators(id, full_name, avatar_url)')
      .in('channel_id', dmChannels.map((c) => c.id))
      .neq('collaborator_id', currentId);

    for (const dm of dmMembers ?? []) {
      const collab = (dm as Record<string, unknown>).collaborators as Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url'> | null;
      if (collab) dmOtherMap.set(dm.channel_id, collab);
    }
  }

  return mapped.map((ch) => ({
    ...ch,
    unreadCount: unreadMap.get(ch.id) ?? 0,
    dmOtherCollaborator: dmOtherMap.get(ch.id),
    lastReadAt: lastReadMap.get(ch.id) ?? null,
  }));
}

export async function getTotalUnreadCount(): Promise<number> {
  const channels = await getChannels();
  return channels.reduce((sum, ch) => sum + (ch.unreadCount ?? 0), 0);
}

export async function createChannel(
  name: string,
  isPrivate = false,
  description?: string,
  emoji = 'hashtag',
): Promise<Channel> {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('discussion_channels')
    .insert({ name, kind: 'global', is_private: isPrivate, created_by: currentId, description: description ?? null, emoji })
    .select()
    .single();

  if (error) throw error;

  const channel = mapChannel(data as unknown as Record<string, unknown>);

  // Auto-add all active collaborators if public
  if (!isPrivate) {
    const { data: collabs } = await supabase
      .from('collaborators')
      .select('id')
      .eq('is_active', true);

    if (collabs?.length) {
      await supabase.from('channel_members').insert(
        collabs.map((c) => ({ channel_id: channel.id, collaborator_id: c.id })),
      );
    }
  } else {
    // Add creator only
    await supabase.from('channel_members').insert({
      channel_id: channel.id,
      collaborator_id: currentId,
    });
  }

  return channel;
}

export async function getOrCreateDmChannel(otherCollaboratorId: string): Promise<Channel> {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) throw new Error('Not authenticated');

  // Look for existing DM between the two collaborators
  const { data: myDmMemberships } = await supabase
    .from('channel_members')
    .select('channel_id')
    .eq('collaborator_id', currentId);

  if (myDmMemberships?.length) {
    const myChannelIds = myDmMemberships.map((m) => m.channel_id);

    const { data: sharedDm } = await supabase
      .from('channel_members')
      .select('channel_id')
      .eq('collaborator_id', otherCollaboratorId)
      .in('channel_id', myChannelIds);

    if (sharedDm?.length) {
      // Check that one of those is actually a DM
      const { data: existing } = await supabase
        .from('discussion_channels')
        .select('*')
        .eq('kind', 'dm')
        .in('id', sharedDm.map((d) => d.channel_id))
        .limit(1)
        .maybeSingle();

      if (existing) return mapChannel(existing as unknown as Record<string, unknown>);
    }
  }

  // Create new DM channel
  const { data, error } = await supabase
    .from('discussion_channels')
    .insert({ name: null, kind: 'dm', is_private: true, created_by: currentId })
    .select()
    .single();

  if (error) throw error;

  const channel = mapChannel(data as unknown as Record<string, unknown>);

  await supabase.from('channel_members').insert([
    { channel_id: channel.id, collaborator_id: currentId },
    { channel_id: channel.id, collaborator_id: otherCollaboratorId },
  ]);

  return channel;
}

// ============================================================
// Messages
// ============================================================

export async function getMessages(
  channelId: string,
  limit = 50,
  before?: string,
): Promise<Message[]> {
  // On veut les `limit` messages les plus récents (ou les plus récents AVANT le
  // curseur `before` pour la pagination) → ORDER DESC + LIMIT, puis on les remet
  // en ordre chronologique côté client pour l'affichage.
  let query = supabase
    .from('messages')
    .select('*, author:collaborators(id, full_name, avatar_url)')
    .eq('channel_id', channelId)
    .order('created_at', { ascending: false });

  // Pagination : ne charger que les messages strictement antérieurs au curseur
  // (created_at du plus ancien message déjà affiché).
  if (before) query = query.lt('created_at', before);

  const { data, error } = await query.limit(limit);

  if (error) throw error;

  return (data ?? [])
    .slice()
    .reverse()
    .map((m) => mapMessage(m as unknown as Record<string, unknown>));
}

/** Messages épinglés d'un salon, le plus récemment épinglé d'abord. */
export async function getPinnedMessages(channelId: string): Promise<Message[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('*, author:collaborators(id, full_name, avatar_url)')
    .eq('channel_id', channelId)
    .not('pinned_at', 'is', null)
    .is('deleted_at', null)
    .order('pinned_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((m) => mapMessage(m as unknown as Record<string, unknown>));
}

/** Épingle ou désépingle un message (tout membre du salon, via la RPC). */
export async function setMessagePinned(messageId: string, pinned: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_message_pinned', {
    p_message_id: messageId,
    p_pinned: pinned,
  });
  if (error) throw error;
}

export async function sendMessage(
  channelId: string,
  body: string,
  parentId?: string,
  allowEmpty?: boolean,
): Promise<Message> {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) throw new Error('Not authenticated');

  // Passe par la RPC `send_message` (SECURITY DEFINER) pour bénéficier
  // de la notification automatique des mentions (côté DB).
  const { data: row, error } = await supabase.rpc('send_message', {
    p_channel_id: channelId,
    p_body: body,
    p_parent_id: parentId ?? undefined,
    p_allow_empty: allowEmpty ?? false,
  });
  if (error) throw error;

  // Re-fetch avec l'auteur joint (la RPC ne retourne que la table messages).
  const { data: full } = await supabase
    .from('messages')
    .select('*, author:collaborators(id, full_name, avatar_url)')
    .eq('id', (row as { id: string }).id)
    .single();

  const message = mapMessage((full ?? row) as unknown as Record<string, unknown>);

  return message;
}

export async function editMessage(id: string, body: string): Promise<void> {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) throw new Error('Not authenticated as a collaborator');

  const { error } = await supabase
    .from('messages')
    .update({ body, is_edited: true, edited_at: new Date().toISOString() })
    .eq('id', id)
    .eq('author_id', currentId);

  if (error) throw error;
}

export async function deleteMessage(id: string): Promise<void> {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) throw new Error('Not authenticated as a collaborator');

  const { error } = await supabase
    .from('messages')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .eq('author_id', currentId);

  if (error) throw error;
}

// ============================================================
// Read tracking
// ============================================================

let markReadTimer: ReturnType<typeof setTimeout> | null = null;

export function markChannelAsRead(channelId: string): void {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) return;

  if (markReadTimer) clearTimeout(markReadTimer);
  markReadTimer = setTimeout(async () => {
    await supabase
      .from('channel_members')
      .update({ last_read_at: new Date().toISOString() })
      .eq('channel_id', channelId)
      .eq('collaborator_id', currentId);
  }, 500);
}

export async function getDmOtherLastReadAt(channelId: string): Promise<string | null> {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) return null;

  const { data, error } = await supabase
    .from('channel_members')
    .select('last_read_at')
    .eq('channel_id', channelId)
    .neq('collaborator_id', currentId)
    .maybeSingle();

  if (error) return null;
  return (data?.last_read_at as string | null) ?? null;
}

export function subscribeToChannelMembers(
  channelId: string,
  onUpdate: () => void,
): RealtimeChannel {
  const channel = supabase
    .channel(`channel_members:${channelId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'channel_members',
        filter: `channel_id=eq.${channelId}`,
      },
      () => onUpdate(),
    )
    .subscribe();

  return channel;
}

// ============================================================
// Channel management (settings, archive, delete, members)
// ============================================================

export interface UpdateChannelParams {
  name?: string;
  description?: string;
  emoji?: string;
  isPrivate?: boolean;
}

export async function updateChannel(id: string, params: UpdateChannelParams): Promise<Channel> {
  const { data, error } = await supabase
    .from('discussion_channels')
    .update({
      ...(params.name !== undefined && { name: params.name }),
      ...(params.description !== undefined && { description: params.description }),
      ...(params.emoji !== undefined && { emoji: params.emoji }),
      ...(params.isPrivate !== undefined && { is_private: params.isPrivate }),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return mapChannel(data as unknown as Record<string, unknown>);
}

export async function archiveChannel(id: string): Promise<void> {
  const { error } = await supabase
    .from('discussion_channels')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
}

export async function deleteChannel(id: string): Promise<void> {
  const { error } = await supabase
    .from('discussion_channels')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function getChannelMembers(channelId: string): Promise<CollaboratorSummary[]> {
  const { data, error } = await supabase
    .from('channel_members')
    .select('collaborators(id, full_name, avatar_url, role)')
    .eq('channel_id', channelId);

  if (error) throw error;

  return (data ?? [])
    .map((row) => (row as Record<string, unknown>).collaborators as CollaboratorSummary)
    .filter(Boolean);
}

export async function addChannelMember(channelId: string, collaboratorId: string): Promise<void> {
  const { error } = await supabase
    .from('channel_members')
    .insert({ channel_id: channelId, collaborator_id: collaboratorId });

  // Ignore duplicate error
  if (error && error.code !== '23505') throw error;
}

export async function removeChannelMember(channelId: string, collaboratorId: string): Promise<void> {
  const { error } = await supabase
    .from('channel_members')
    .delete()
    .eq('channel_id', channelId)
    .eq('collaborator_id', collaboratorId);

  if (error) throw error;
}

// ============================================================
// Collaborators (for DM + mentions)
// ============================================================

export async function getCollaborators(): Promise<CollaboratorSummary[]> {
  const currentId = getCurrentCollaboratorId();

  const { data, error } = await supabase
    .from('collaborators')
    .select('id, full_name, avatar_url, role')
    .eq('is_active', true)
    .neq('id', currentId ?? '')
    .order('full_name', { ascending: true });

  if (error) throw error;
  return (data ?? []) as CollaboratorSummary[];
}

// ============================================================
// Mentions (G-05)
// ============================================================

// Cache collaborator list for mention parsing (refreshed when stale)
let collaboratorCache: CollaboratorSummary[] = [];

export async function refreshCollaboratorCache(): Promise<void> {
  const { data } = await supabase
    .from('collaborators')
    .select('id, full_name, avatar_url, role')
    .eq('is_active', true);
  collaboratorCache = (data ?? []) as CollaboratorSummary[];
}

export function parseMentions(body: string): string[] {
  // Match @FirstName or @Full Name (up to 3 words)
  const mentionRegex = /@([\wÀ-ɏ]+(?: [\wÀ-ɏ]+){0,2})/gi;
  const found = new Set<string>();
  let match;

  while ((match = mentionRegex.exec(body)) !== null) {
    const mentioned = (match[1] ?? '').toLowerCase();
    for (const collab of collaboratorCache) {
      if (collab.full_name.toLowerCase().includes(mentioned)) {
        found.add(collab.id);
      }
    }
  }

  return Array.from(found);
}

// ============================================================
// Realtime
// ============================================================

export function subscribeToMessages(
  channelId: string,
  onMessage: (message: Message) => void,
): RealtimeChannel {
  const channel = supabase
    .channel(`messages:${channelId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'messages',
        filter: `channel_id=eq.${channelId}`,
      },
      async (payload) => {
        console.log('[Realtime] messages event', payload.eventType, payload);
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const row = payload.new as Record<string, unknown>;
          // Try to enrich with author info from cache, then refetch if missing
          const cachedAuthor = collaboratorCache.find((c) => c.id === row.author_id);
          if (cachedAuthor) {
            onMessage(mapMessage({ ...row, author: cachedAuthor }));
          } else {
            // Fallback: fetch author separately
            const { data } = await supabase
              .from('collaborators')
              .select('id, full_name, avatar_url')
              .eq('id', row.author_id as string)
              .single();
            onMessage(mapMessage({ ...row, author: data ?? undefined }));
          }
        }
      },
    )
    .subscribe((status, err) => {
      console.log('[Realtime] messages subscription status', status, err ?? '');
    });

  return channel;
}

export function unsubscribeFromMessages(channel: RealtimeChannel): void {
  supabase.removeChannel(channel);
}

// ============================================================
// Dossier channels (EPIC G-07)
// ============================================================

export async function getDossierChannel(dossierId: string): Promise<Channel | null> {
  const { data, error } = await supabase
    .from('discussion_channels')
    .select('*')
    .eq('dossier_id', dossierId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return mapChannel(data as unknown as Record<string, unknown>);
}

/** Non-lus d’autrui après last_read_at. 0 si pas membre du canal. */
export async function getChannelUnreadCount(channelId: string): Promise<number> {
  const currentId = getCurrentCollaboratorId();
  if (!currentId) return 0;

  const { data: membership } = await supabase
    .from('channel_members')
    .select('last_read_at')
    .eq('channel_id', channelId)
    .eq('collaborator_id', currentId)
    .maybeSingle();

  if (!membership) return 0;

  const lastRead = membership.last_read_at ?? new Date(0).toISOString();
  const { count } = await supabase
    .from('messages')
    .select('id', { count: 'exact', head: true })
    .eq('channel_id', channelId)
    .is('deleted_at', null)
    .gt('created_at', lastRead)
    .neq('author_id', currentId);

  return count ?? 0;
}

export interface DossierChannelMember {
  collaboratorId: string;
  fullName: string;
  avatarUrl: string | null;
  role: string;
  source: 'auto' | 'manual';
  joinedAt: string;
}

export async function getDossierChannelMembers(
  channelId: string,
): Promise<DossierChannelMember[]> {
  const { data, error } = await supabase
    .from('channel_members')
    .select('collaborator_id, source, joined_at, collaborators(id, full_name, avatar_url, role)')
    .eq('channel_id', channelId);
  if (error) throw error;
  return (data ?? []).map((row) => {
    const c = (row as Record<string, unknown>).collaborators as
      | Pick<Tables<'collaborators'>, 'id' | 'full_name' | 'avatar_url' | 'role'>
      | null;
    return {
      collaboratorId: row.collaborator_id as string,
      fullName: c?.full_name ?? '—',
      avatarUrl: c?.avatar_url ?? null,
      role: c?.role ?? '',
      source: (row.source as 'auto' | 'manual') ?? 'manual',
      joinedAt: row.joined_at as string,
    };
  });
}

export async function inviteManualMember(channelId: string, collaboratorId: string): Promise<void> {
  const { error } = await supabase
    .from('channel_members')
    .insert({ channel_id: channelId, collaborator_id: collaboratorId, source: 'manual' });
  if (error) throw error;
}

export async function removeManualMember(channelId: string, collaboratorId: string): Promise<void> {
  const { error } = await supabase
    .from('channel_members')
    .delete()
    .eq('channel_id', channelId)
    .eq('collaborator_id', collaboratorId)
    .eq('source', 'manual');
  if (error) throw error;
}
